import { useEffect, useId, useState } from 'react';

const GRADIENT_FROM = '#5FD5DE';
const GRADIENT_TO = '#C0DE60';
const COMPLETE_ALPHA = 0.6;
const ARC_DURATION_MS = 900;

/**
 * @param size - The size of the circular progress in pixels.
 * @param percentage - The percentage of the circular progress.
 * @param bgColor - The background color of the circular progress.
 * @param bgTrackColor - The background color of the track of the circular progress.
 * @param strokeWidth - The width of the stroke of the circular progress.
 */
export type CircularProgressProps = {
  size: number;
  percentage: number;
  bgColor: string;
  bgTrackColor: string;
  strokeWidth: number;
  imgUrl: string;
  animate: 'none' | 'forward';
  /** Describes the image for screen readers — a course cover is not a user avatar. */
  imgAlt?: string;
};

export const DEFAULTS: CircularProgressProps = {
  size: 140,
  percentage: 0,
  bgColor: '#ffffff',
  bgTrackColor: '#f5f5f7',
  strokeWidth: 12,
  imgUrl: '/user-default.svg',
  animate: 'forward'
};

const calculateOffset = (c: number, percentage: number) => {
  return c * (1 - percentage / 100);
};

/**
 * @param size - The size of the circular progress in pixels.
 * @param percentage - The percentage of the circular progress.
 * @param bgColor - The background color of the circular progress.
 * @param bgTrackColor - The background color of the track of the circular progress.
 * @param strokeWidth - The width of the stroke of the circular progress.
 */
export function CircularProgress({
  size = DEFAULTS.size,
  percentage = DEFAULTS.percentage,
  bgColor = DEFAULTS.bgColor,
  bgTrackColor = DEFAULTS.bgTrackColor,
  strokeWidth = DEFAULTS.strokeWidth,
  imgUrl = DEFAULTS.imgUrl,
  animate = DEFAULTS.animate,
  imgAlt = ''
}: CircularProgressProps) {
  const p = Math.max(0, Math.min(100, percentage)); // Clamp the percentage between 0 and 100.
  const isComplete = p >= 100;

  const from = GRADIENT_FROM;
  const to = GRADIENT_TO;

  const r = (size - strokeWidth) / 2; // Radius -> from middle of the circle to the edge
  const c = 2 * Math.PI * r; // Circumference of the circle
  const offset = calculateOffset(c, p); // Offset for the progress

  /**
   * `forward` starts the arc empty so it sweeps up to the target on mount; `none` starts at the target.
   */
  const [currentOffset, setCurrentOffset] = useState<number>(animate === 'forward' ? c : offset);

  /**
   * Whether the arc has finished travelling. `isComplete` only describes the target, so dimming on it
   * alone would fade the ring while it is still sweeping towards 100%.
   */
  const [hasArcSettled, setHasArcSettled] = useState<boolean>(animate === 'none');

  useEffect(() => {
    if (animate === 'none') {
      setHasArcSettled(true);
      setCurrentOffset(offset);
      return;
    }

    setHasArcSettled(false);
    const frame = requestAnimationFrame(() => setCurrentOffset(offset));

    return () => cancelAnimationFrame(frame);
  }, [offset, animate]);

  const uid = useId();
  const gradId = `grad-${uid}`;
  const innerShadowId = `innerShadow-${uid}`;

  const sizeInnerContent = size - strokeWidth * 2;

  return (
    <div className="relative flex items-center justify-center rounded-full" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="block">
        <defs>
          {/* Gradient for the progress */}
          <linearGradient id={gradId} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>

          <filter id={innerShadowId} x="-50%" y="-50%" width="200%" height="200%">
            <feOffset in="SourceAlpha" dx="-4" dy="0" result="offset" />
            <feGaussianBlur in="offset" stdDeviation="2" result="blur" />
            <feComposite in="blur" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="innerShadowAlpha" />
            <feColorMatrix
              in="innerShadowAlpha"
              type="matrix"
              values="
                0 0 0 0 0
                0 0 0 0 0
                0 0 0 0 0
                0 0 0 0.5 0
              "
              result="coloredShadow"
            />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="coloredShadow" />
            </feMerge>
          </filter>
        </defs>

        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={bgTrackColor}
          strokeWidth={strokeWidth}
          filter={`url(#${innerShadowId})`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />

        {/* Progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          filter={`url(#${innerShadowId})`}
          stroke={`url(#${gradId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={currentOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          opacity={isComplete && hasArcSettled ? COMPLETE_ALPHA : 1}
          className="circular-progress-arc"
          // Only the arc's own travel counts — the opacity transition would otherwise settle itself.
          onTransitionEnd={(event) => {
            if (event.propertyName === 'stroke-dashoffset') setHasArcSettled(true);
          }}
          style={{
            transition:
              animate === 'none' ? undefined : `stroke-dashoffset ${ARC_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease-in-out`
          }}
        />
      </svg>

      {/* Inner Content */}
      <div
        className={`absolute rounded-full flex items-center justify-center`}
        style={{
          width: sizeInnerContent,
          height: sizeInnerContent,
          background: bgColor,
          padding: sizeInnerContent / 4
        }}>
        <img src={imgUrl} alt={imgAlt} className="w-full h-full object-cover" />
      </div>
    </div>
  );
}
